/**
 * posteingang-api.ts - display helpers for the Eingangsliste (epic B2).
 *
 * Pure mapping only: the German labels and the SQL that derives status all live in
 * `@brokly/shared/posteingang`, so nothing here re-decides what an Eingang IS. What is
 * genuinely a frontend choice - which StatusDot tone a status wears, how a `numeric`
 * confidence string becomes a percent - lives here and nowhere else.
 *
 * [Source: packages/shared/src/posteingang.ts] (labels + status derivation)
 * [Source: apps/web/src/features/aufgaben/api/aufgaben-api.ts] (the tone-map pattern)
 */
import { POSTEINGANG_STATUS, type PosteingangStatus } from '@brokly/shared';
import type { StatusTone } from '../../../components/ui/StatusDot';

/**
 * Status -> StatusDot tone. `freigegeben` is in the vocabulary but has no producer
 * until the staged Freigabe (epic E1); it gets a tone anyway so the day it starts
 * arriving the list renders it instead of falling through to neutral.
 */
export const POSTEINGANG_STATUS_TONE: Record<PosteingangStatus, StatusTone> = {
  [POSTEINGANG_STATUS.OFFEN]: 'neutral',
  [POSTEINGANG_STATUS.IN_BEARBEITUNG]: 'info',
  [POSTEINGANG_STATUS.FREIGABE_AUSSTEHEND]: 'warning',
  [POSTEINGANG_STATUS.FREIGEGEBEN]: 'info',
  [POSTEINGANG_STATUS.ERLEDIGT]: 'success',
};

/**
 * The wire sends confidence as a Postgres `numeric` string ('0.920') or null - null
 * meaning "no score", which is NOT the same as zero and must not render as 0 %.
 * Detailkonzept 8.1 asks for a Confidence-Indikator per Eingang.
 */
export function konfidenzPercent(confidence: string | null): number | null {
  if (confidence === null) return null;
  const parsed = Number(confidence);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
}
